import React, { useState } from 'react';
import { UploadFile } from 'antd/lib/upload/interface';
import { UploadOutlined } from '@ant-design/icons';
import { RcFile, UploadChangeParam } from 'antd/lib/upload';
import { Button, message, Upload } from 'antd';
import { AddedAttribute } from 'pages/UserPage/components/MiddlePane/types';
import { ASSET_UPLOAD_URL } from 'common/constants/constants';
import { createAssetUrl } from 'common/utils/http';

interface FileUploadFields {
  uuid: string;
  filename: string;
  id: number;
}

const parseInitialFileList = (attributes: AddedAttribute[]): FileUploadFields[] => {
  if (attributes?.length === 0 || attributes[0].value === '') return [];
  return attributes.map(attr => ({
    ...JSON.parse(attr.value as string),
    id: attr.id || -1,
  }));
};

interface Props {
  fileAttributes: AddedAttribute[];
  isImage?: boolean;
  onRemove: (removeInd: number) => void;
  onUpload: (imgUuid: string, imgFilename: string) => void;
  singleton?: boolean;
  checkFileType?: (file: RcFile) => boolean;
}

const FileUpload = ({
  fileAttributes,
  isImage = false,
  onUpload,
  onRemove,
  singleton,
  checkFileType = () => true,
}: Props) => {
  const defaultFilelist = parseInitialFileList(fileAttributes)
    .filter(metadata => metadata.id !== -1)
    .map(metadata => ({
      uid: metadata.uuid,
      name: metadata.filename,
      size: 10,
      type: isImage ? 'image/*' : 'application/octet-stream',
      thumbUrl: isImage ? createAssetUrl(metadata.id) : undefined,
    }));
  const [fileList, setFileList] = useState<UploadFile[]>(defaultFilelist);

  const beforeUpload = (file: RcFile) => {
    const shouldUploadFile = checkFileType(file);
    if (!shouldUploadFile) {
      message.error(`${file.type} is the wrong file type`);
    }

    return shouldUploadFile;
  };

  const onNewFile = (info: UploadChangeParam) => {
    if (info.file.status === 'uploading') {
      setFileList(singleton ? [info.file] : info.fileList);
      return;
    }

    if (info.file.status === 'done') {
      message.success(`${info.file.name} file uploaded successfully`);
      onUpload(info.file.response.uuid, info.file.name || '');
    } else if (info.file.status === 'error') {
      message.error(`${info.file.name} file upload failed.`);
    }
  };

  const onRemoveFile = (removedFile: UploadFile) => {
    onRemove(fileList.findIndex(f => f.uid === removedFile.uid));
    setFileList(fileList.filter(f => f.uid !== removedFile.uid));
  };

  return (
    <Upload
      action={ASSET_UPLOAD_URL}
      beforeUpload={beforeUpload}
      fileList={fileList}
      listType="picture"
      onChange={onNewFile}
      onRemove={onRemoveFile}
    >
      <Button>
        <UploadOutlined /> Click to upload
      </Button>
    </Upload>
  );
};

export default FileUpload;
